import thresholdsData from '../data/thresholds.json';

/**
 * Get temperature status based on thresholds
 * @param {number} temperature - Current temperature
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @returns {string} - 'normal', 'warning', or 'critical'
 */
export const getTemperatureStatus = (temperature, vehicleId) => {
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  
  if (temperature < thresholds.min_critical || temperature > thresholds.max_critical) {
    return 'critical';
  }
  
  if (temperature < thresholds.min_warning || temperature > thresholds.max_warning) {
    return 'warning';
  }
  
  return 'normal';
};

/**
 * Get status color classes
 * @param {string} status - Status string
 * @returns {object} - Tailwind color classes
 */
export const getStatusColor = (status) => {
  const colors = {
    normal: {
      bg: 'bg-success-100 dark:bg-success-900/20',
      text: 'text-success-700 dark:text-success-400',
      border: 'border-success-500',
      badge: 'bg-success-500',
    },
    warning: {
      bg: 'bg-warning-100 dark:bg-warning-900/20',
      text: 'text-warning-700 dark:text-warning-400',
      border: 'border-warning-500',
      badge: 'bg-warning-500',
    },
    critical: {
      bg: 'bg-danger-100 dark:bg-danger-900/20',
      text: 'text-danger-700 dark:text-danger-400',
      border: 'border-danger-500',
      badge: 'bg-danger-500',
    },
  };
  
  return colors[status] || colors.normal;
};

/**
 * Format temperature for display
 * @param {number} temperature - Temperature value
 * @param {string} unit - 'C' or 'F'
 * @returns {string} - Formatted temperature string
 */
export const formatTemperature = (temperature, unit = 'C') => {
  if (temperature === null || temperature === undefined) {
    return '--';
  }

  const numericTemp = Number(
    unit === 'F' ? celsiusToFahrenheit(temperature) : temperature
  );

  if (Number.isNaN(numericTemp)) {
    return '--';
  }

  return `${numericTemp}°${unit}`;
};

/**
 * Convert Celsius to Fahrenheit
 * @param {number} celsius - Temperature in Celsius
 * @returns {number} - Temperature in Fahrenheit
 */
export const celsiusToFahrenheit = (celsius) => {
  return (celsius * 9/5) + 32;
};

/**
 * Convert Fahrenheit to Celsius
 * @param {number} fahrenheit - Temperature in Fahrenheit
 * @returns {number} - Temperature in Celsius
 */
export const fahrenheitToCelsius = (fahrenheit) => {
  return (fahrenheit - 32) * 5/9;
};

/**
 * Get temperature trend indicator
 * @param {Array} readings - Array of temperature readings
 * @returns {string} - 'up', 'down', or 'stable'
 */
export const getTemperatureTrend = (readings) => {
  if (!readings || readings.length < 2) return 'stable';
  
  const recent = readings.slice(-5);
  const avg = recent.reduce((sum, r) => sum + r.temperature, 0) / recent.length;
  const lastTemp = recent[recent.length - 1].temperature;
  
  if (lastTemp > avg + 0.5) return 'up';
  if (lastTemp < avg - 0.5) return 'down';
  return 'stable';
};

/**
 * Calculate temperature statistics
 * @param {Array} readings - Array of temperature readings
 * @returns {object} - Statistics object
 */
export const calculateStats = (readings) => {
  if (!readings || readings.length === 0) {
    return {
      min: null,
      max: null,
      avg: null,
      current: null,
    };
  }
  
  const temps = readings.map(r => r.temperature);
  return {
    min: Math.min(...temps),
    max: Math.max(...temps),
    avg: temps.reduce((sum, t) => sum + t, 0) / temps.length,
    current: temps[temps.length - 1],
  };
};

/**
 * Calculate compliance percentage
 * @param {Array} readings - Array of temperature readings
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @returns {number} - Compliance percentage (0-100)
 */
export const calculateCompliance = (readings, vehicleId) => {
  if (!readings || readings.length === 0) return 0;
  
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  const compliantReadings = readings.filter(
    r => r.temperature >= thresholds.optimal_min && r.temperature <= thresholds.optimal_max
  );
  
  return (compliantReadings.length / readings.length) * 100;
};

/**
 * Calculate the variation between a temperature reading and the previous one
 * @param {number|null|undefined} currentTemperature - Current temperature reading
 * @param {number|null|undefined} previousTemperature - Previous temperature reading
 * @returns {{delta: number, direction: 'up'|'down'|'stable', formatted: string}|null}
 *   Delta info, or null when either reading is missing/invalid
 */
export const getTemperatureDelta = (currentTemperature, previousTemperature) => {
  if (
    currentTemperature === null || currentTemperature === undefined ||
    previousTemperature === null || previousTemperature === undefined
  ) {
    return null;
  }

  const current = Number(currentTemperature);
  const previous = Number(previousTemperature);

  if (Number.isNaN(current) || Number.isNaN(previous)) {
    return null;
  }

  const delta = current - previous;
  const direction = delta > 0.05 ? 'up' : delta < -0.05 ? 'down' : 'stable';
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';

  return {
    delta,
    direction,
    formatted: `${sign}${Math.abs(delta).toFixed(1)}°C`,
  };
};

/**
 * Check if alert should be triggered
 * @param {Array} readings - Recent temperature readings
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @returns {object|null} - Alert object or null
 */
export const checkForAlert = (readings, vehicleId) => {
  if (!readings || readings.length === 0) return null;
  
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  const lastReading = readings[readings.length - 1];
  const temp = lastReading.temperature;
  
  // Critical temperature
  if (temp > thresholds.max_critical) {
    return {
      type: 'CRITICAL_HIGH',
      severity: 'critical',
      message: `Critical high temperature: ${formatTemperature(temp)}`,
    };
  }
  
  if (temp < thresholds.min_critical) {
    return {
      type: 'CRITICAL_LOW',
      severity: 'critical',
      message: `Critical low temperature: ${formatTemperature(temp)}`,
    };
  }
  
  // High temperature (sustained)
  const recentHighReadings = readings.slice(-5).filter(r => r.temperature > thresholds.max_warning);
  if (recentHighReadings.length >= 3) {
    return {
      type: 'HIGH_TEMP',
      severity: 'warning',
      message: `High temperature detected: ${formatTemperature(temp)}`,
    };
  }
  
  // Low temperature (sustained)
  const recentLowReadings = readings.slice(-5).filter(r => r.temperature < thresholds.min_warning);
  if (recentLowReadings.length >= 3) {
    return {
      type: 'LOW_TEMP',
      severity: 'warning',
      message: `Low temperature detected: ${formatTemperature(temp)}`,
    };
  }
  
  // Rapid change
  if (readings.length >= 3) {
    const recent = readings.slice(-3);
    const tempChange = Math.abs(recent[0].temperature - recent[recent.length - 1].temperature);
    if (tempChange >= 3) {
      return {
        type: 'RAPID_CHANGE',
        severity: 'warning',
        message: `Rapid temperature change: ${tempChange.toFixed(1)}°C`,
      };
    }
  }
  
  return null;
};

